'use client';

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addItemToCartMutation } from '@frodo/lib/services/http/client/api-cart.client';
import { QUERY_KEYS } from '@frodo/lib/services/http/shared/queryKeys';
import { productSingleClientQuery } from '@frodo/lib/services/http/client/api-product.client';
import { useGa4Events } from '@frodo/lib/services/ga4';
import { useAlertToast } from '@frodo/lib/hooks/domain/useAlertToast';
import type { ActionResponse, Product } from '@frodo/models/types';
import { clientLoggerService } from '@frodo/lib/services/logger/clientLogger.service';
import { getActionErrorMessage } from '@frodo/lib/utils/client';

export type AddToCartTrackingContext = {
  widget?: string;
  position?: number;
};

type AddToCartVariables = {
  sku: string;
  quantity: number;
  isBundle?: boolean;
};

export type UseAddToCartOptions = {
  product: Product;
  trackingContext?: AddToCartTrackingContext;
  ga4Tracking?: boolean;
  showSuccessAlertToast?: boolean;
  showErrorAlertToast?: boolean;
  onSuccess?: (data: ActionResponse, variables: AddToCartVariables) => void;
  onError?: (error: unknown) => void;
};

export type UseAddToCartResult = {
  addToCart: (quantity?: number) => void;
  isPending: boolean;
};

export const useAddToCart = ({
  product,
  trackingContext,
  ga4Tracking = true,
  showSuccessAlertToast = true,
  showErrorAlertToast = true,
  onSuccess,
  onError,
}: UseAddToCartOptions): UseAddToCartResult => {
  const queryClient = useQueryClient();
  const notifyAlertToast = useAlertToast(state => state.notify);
  const { trackAddToCart } = useGa4Events();

  const isBundle = product.variety?.toLowerCase() === 'bundle';

  const { mutate, isPending } = useMutation({
    ...addItemToCartMutation(),
    onSuccess: async (data, variables) => {
      // TODO: the cart query cancellation is only for all product widgets sliders in cart page,
      // it should be moveed to corresponding component.
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.cart() });
      queryClient.resetQueries({
        queryKey: QUERY_KEYS.deletedCartItem(),
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cart() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userInfo() });

      if (ga4Tracking) {
        queryClient
          .fetchQuery(productSingleClientQuery(product.sku))
          .then((freshProduct: Product | null) => {
            if (!freshProduct) return;

            trackAddToCart(freshProduct, variables.quantity, trackingContext);
          })
          .catch((error: unknown) => {
            clientLoggerService.error(
              error,
              'failed to fetch product for tracking',
            );
          });
      }

      if (data.success) {
        if (showSuccessAlertToast && data.messages) {
          notifyAlertToast(data.messages);
        }
        onSuccess?.(data, variables);
      } else {
        if (showErrorAlertToast && data.message?.text) {
          notifyAlertToast({
            text: data.message.text,
            type: 'error',
          });
        }
        onError?.(data.message?.text);
      }
    },
    onError: error => {
      if (showErrorAlertToast) {
        notifyAlertToast(getActionErrorMessage(error));
      }

      onError?.(error);
    },
  });

  const addToCart = useCallback(
    (quantity: number = 1) => {
      mutate({ sku: product.sku, quantity, isBundle });
    },
    [mutate, product.sku, isBundle],
  );

  return { addToCart, isPending };
};
